import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, AlertTriangle, Eye, EyeOff, Flag, ChevronDown, ChevronUp, Square, CheckSquare2 } from 'lucide-react';
import { ClientActionItemModal, ActionItem } from './ClientActionItemModal';

interface ActionItemStats {
  total: number;
  not_started: number;
  in_progress: number;
  completed: number;
  overdue: number;
  due_soon: number;
}

/**
 * CLIENT ACTION ITEMS PANEL
 *
 * Client view of action items - based on staff ActionItemsPanel.
 * Shows tasks assigned to the client with ability to mark complete.
 *
 * Key differences from staff version:
 * - No "Add Task" button (clients don't create AIs)
 * - Checkbox to LEFT of flag for quick-complete
 * - Click on item opens read-only modal (can only change status)
 * - Same priority colors, sorting, due date highlighting
 *
 * Design principles (same as staff):
 * - Title only in list view (details in modal)
 * - Yellow title = due within 3 days
 * - Red title = overdue
 * - Sorted by: priority -> due date -> alphabetical (backend handles this)
 */
export const ClientActionItemsPanel: React.FC = () => {
  const [items, setItems] = useState<ActionItem[]>([]);
  const [stats, setStats] = useState<ActionItemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ActionItem | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);

  // Collapse state - persist to localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('clientActionItemsCollapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('clientActionItemsCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  const fetchActionItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/my/action-items?include_completed=true`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch action items');
      }

      const data = await response.json();
      setItems(data.actionItems || []);

      // Calculate stats from items
      const allItems = data.actionItems || [];
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const calculatedStats: ActionItemStats = {
        total: allItems.length,
        not_started: allItems.filter((i: ActionItem) => i.status === 'not_started').length,
        in_progress: allItems.filter((i: ActionItem) => i.status === 'in_progress').length,
        completed: allItems.filter((i: ActionItem) => i.status === 'completed').length,
        overdue: allItems.filter((i: ActionItem) => {
          if (!i.due_date || i.status === 'completed') return false;
          const due = new Date(i.due_date);
          due.setHours(0, 0, 0, 0);
          return due < now;
        }).length,
        due_soon: allItems.filter((i: ActionItem) => {
          if (!i.due_date || i.status === 'completed') return false;
          const due = new Date(i.due_date);
          due.setHours(0, 0, 0, 0);
          const daysUntil = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          return daysUntil >= 0 && daysUntil <= 3;
        }).length
      };

      setStats(calculatedStats);
      setError(null);
    } catch (err) {
      console.error('Error fetching action items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load action items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  const handleItemClick = (item: ActionItem) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleModalSave = () => {
    fetchActionItems();
    handleModalClose();
  };

  // Quick-complete handler (checkbox click)
  const handleQuickComplete = async (e: React.MouseEvent, item: ActionItem) => {
    e.stopPropagation(); // Don't open modal

    if (item.status === 'completed') return; // Already completed

    setCompletingId(item.id);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/my/action-items/${item.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'completed' })
      });

      if (!response.ok) {
        throw new Error('Failed to complete action item');
      }

      // Refresh the list
      fetchActionItems();
    } catch (err) {
      console.error('Error completing action item:', err);
    } finally {
      setCompletingId(null);
    }
  };

  // Determine due date status for title coloring
  const getDueDateStatus = (dueDate: string | null, status: string): 'normal' | 'due-soon' | 'overdue' | 'completed' => {
    if (status === 'completed') return 'completed';
    if (!dueDate) return 'normal';

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 3) return 'due-soon';
    return 'normal';
  };

  if (loading) {
    return (
      <div className={`card action-items-panel ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <h3><CheckSquare size={16} /> Action Items</h3>
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="loading-state">
            <span className="loading-spinner" />
            <span>Loading...</span>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`card action-items-panel ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <h3><CheckSquare size={16} /> Action Items</h3>
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="error-state">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Filter items based on completion status
  const activeItems = items.filter(item => item.status !== 'completed');
  const completedItems = items.filter(item => item.status === 'completed');
  const completedCount = completedItems.length;

  return (
    <div className={`card action-items-panel client-action-items ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <h3>
          <CheckSquare size={16} /> Action Items
          {activeItems.length > 0 && (
            <span className="count">({activeItems.length})</span>
          )}
        </h3>
        <div className="header-buttons">
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {!isCollapsed && (activeItems.length === 0 && completedCount === 0 ? (
        <div className="empty-state success">
          <CheckSquare2 size={18} />
          <span>All caught up!</span>
        </div>
      ) : (
        <div className="action-items-list">
          {/* Active items */}
          {activeItems.map((item) => {
            const dueDateStatus = getDueDateStatus(item.due_date, item.status);
            const flagColorClass = `flag-${item.priority}`;
            const rowClasses = [
              'action-item-row',
              dueDateStatus !== 'normal' ? `row-${dueDateStatus}` : ''
            ].filter(Boolean).join(' ');
            const isCompleting = completingId === item.id;

            return (
              <div
                key={item.id}
                className={rowClasses}
                onClick={() => handleItemClick(item)}
              >
                {/* Checkbox for quick-complete */}
                <button
                  className={`item-checkbox ${isCompleting ? 'completing' : ''}`}
                  onClick={(e) => handleQuickComplete(e, item)}
                  title="Mark as complete"
                  disabled={isCompleting}
                >
                  <Square size={16} />
                </button>
                <Flag size={14} className={`action-item-flag ${flagColorClass}`} />
                <span className="action-item-title">{item.title}</span>
              </div>
            );
          })}

          {/* Divider and completed items */}
          {showCompleted && completedItems.length > 0 && (
            <>
              <div className="action-items-divider">
                <span>Completed</span>
              </div>
              {completedItems.map((item) => (
                <div
                  key={item.id}
                  className="action-item-row row-completed"
                  onClick={() => handleItemClick(item)}
                >
                  <span className="item-checkbox completed">
                    <CheckSquare2 size={16} />
                  </span>
                  <Flag size={14} className={`action-item-flag flag-${item.priority} flag-completed`} />
                  <span className="action-item-title completed">
                    {item.title}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      ))}

      {/* Stats bar - show stats and completed toggle */}
      {!isCollapsed && (stats && (stats.overdue > 0 || stats.due_soon > 0 || completedCount > 0)) && (
        <div className="action-items-stats">
          <div className="stats-left">
            {stats.overdue > 0 && (
              <span className="stat overdue">
                <AlertTriangle size={12} /> {stats.overdue} overdue
              </span>
            )}
            {stats.due_soon > 0 && (
              <span className="stat due-soon">
                {stats.due_soon} due soon
              </span>
            )}
          </div>
          {completedCount > 0 && (
            <button
              className={`show-completed-btn ${showCompleted ? 'active' : ''}`}
              onClick={() => setShowCompleted(!showCompleted)}
              title={showCompleted ? 'Hide completed' : 'Show completed'}
            >
              {showCompleted ? <EyeOff size={12} /> : <Eye size={12} />}
              <span>{completedCount} done</span>
            </button>
          )}
        </div>
      )}

      {showModal && selectedItem && (
        <ClientActionItemModal
          item={selectedItem}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      <style>{`
        /* Client-specific action items styles - matches staff list appearance */
        .client-action-items .item-checkbox {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          padding: 0;
          border: none;
          background: transparent;
          color: var(--text-tertiary);
          cursor: pointer;
          border-radius: var(--radius-small);
          transition: all 0.15s ease;
          flex-shrink: 0;
        }

        .client-action-items .item-checkbox:hover {
          color: var(--accent-green);
          background: rgba(52, 199, 89, 0.1);
        }

        .client-action-items .item-checkbox.completing {
          opacity: 0.5;
          cursor: wait;
        }

        .client-action-items .item-checkbox.completed {
          color: var(--accent-green);
          cursor: default;
        }

        /* Match staff list style - simple list, no borders on individual items */
        .client-action-items .action-items-list {
          display: flex;
          flex-direction: column;
          padding: 6px 0;
          margin: 0;
        }

        .client-action-items .action-item-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 5px 16px;
          cursor: pointer;
          transition: background 0.15s ease;
          border: none !important;
          border-radius: 0 !important;
          background: transparent !important;
        }

        .client-action-items .action-item-row:hover {
          background: var(--bg-tertiary) !important;
        }

        .client-action-items .action-item-row.row-completed {
          opacity: 0.7;
        }

        .client-action-items .action-item-row.row-completed:hover {
          opacity: 1;
        }

        .client-action-items .empty-state.success {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 16px;
          text-align: center;
          color: var(--accent-green);
          font-size: 13px;
        }

        .client-action-items .empty-state.success svg {
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default ClientActionItemsPanel;
